import {
  CloseButton,
  Flex,
  Select,
  SimpleGrid,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useUpdateRRAssigned } from "../../../api/hooks/creditLimit/useUpdateRRAssigned.ts";
import { useLimitRequests } from "../../../api/hooks/limitRequest/useLimitRequests.ts";
import { useRRAssignees } from "../../../api/hooks/user/useRRAssignees.ts";
import SectionTitle from "../../../components/Section/SectionTitle/SectionTitle.tsx";
import Table from "../../../components/Table/Table.tsx";
import { SortDirection, TableRow } from "../../../components/Table/types.ts";
import { AppRoute } from "../../../constants.ts";
import {
  LimitRequestFilter,
  LimitRequestOrderBy,
  LimitRequestStatusFilterOptions,
  OrderBy,
  StringFilterMode,
  UpdateRrAssignedInput,
} from "../../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { CustomerOption } from "../../../types/customer.ts";
import {
  LimitRequest,
  limitRequestTypeOptions,
} from "../../../types/limitRequest/limitRequest.ts";
import { getCompanyOptions } from "../../../utils/company.ts";
import { getLimitRequestApiFilter } from "../../../utils/filter.ts";
import { getPagesCount } from "../../../utils/pagination.ts";
import { getCURecommendationApiSort } from "../utils.ts";
import { useNewCURecommendationTableData } from "./hooks/useNewCURecommendationTableData.tsx";

interface Props {
  companies: CustomerOption[] | undefined;
}

const NewCURecommendationTable = ({ companies }: Props) => {
  const navigate = useNavigate();

  const [orderBy, setOrderBy] = useState<LimitRequestOrderBy>({
    createdAt: OrderBy.Desc,
  });
  const [filter, setFilter] = useState<LimitRequestFilter>({});
  const [search, setSearch] = useState<string | undefined>();
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState<number>(1);

  const [{ data, fetching, error }, refreshData] = useLimitRequests({
    filter: {
      ...filter,
      requestStatus: {
        in: [
          LimitRequestStatusFilterOptions.OngoingCuManagerReview,
          LimitRequestStatusFilterOptions.OngoingCu,
        ],
      },
      limitName: debouncedSearch
        ? {
            contains: debouncedSearch,
            mode: StringFilterMode.Insensitive,
          }
        : undefined,
    },
    orderBy,
    page,
  });

  const [{ data: assigneesData }] = useRRAssignees();

  const assignees = assigneesData?.users?.data;

  const [, updateRRAssigned] = useMutationNotificationWrapper(
    useUpdateRRAssigned(),
    {
      success: { message: "RR Assigned has been successfully updated." },
    }
  );

  const newCURecommendations: LimitRequest[] | undefined | null =
    data?.limitRequests?.data;

  const pageCount = getPagesCount(data?.limitRequests?.total);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getCURecommendationApiSort(sort));
    },
    [setOrderBy]
  );

  const handleEditRRAssigned = useCallback(
    (id: number, input: UpdateRrAssignedInput) => {
      updateRRAssigned({ id, input }).then(refreshData);
    },
    [updateRRAssigned, refreshData]
  );

  const handleRowClik = useCallback(
    (record: TableRow) => {
      navigate(`${AppRoute.LimitRequest}/${record.id}`);
    },
    [navigate]
  );

  const [columns, rows] = useNewCURecommendationTableData({
    data: newCURecommendations,
    assignees,
    onEditRRAssigned: handleEditRRAssigned,
  });

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilter(getLimitRequestApiFilter({ [key]: value }));
  }, []);

  if (error) {
    notifications.clean();
    notifications.show({
      message:
        "Something went wrong while trying to fetch New CU Recommendation data.",
      variant: "error",
    });
  }

  return (
    <Flex direction="column" gap={12}>
      <SectionTitle>New CU Recommendation</SectionTitle>
      <SimpleGrid
        cols={3}
        breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        spacing={20}
        verticalSpacing={12}
        pb={8}
      >
        <Select
          size="m"
          clearable
          searchable
          label="Company name"
          placeholder="Select Company name"
          data={getCompanyOptions(companies)}
          onChange={(value) => handleFilterChange("companyName", value)}
        />
        <Select
          size="m"
          clearable
          label="Limit Type"
          placeholder="Select Limit Type"
          data={limitRequestTypeOptions}
          onChange={(value) => handleFilterChange("limitRequestType", value)}
        />
        <TextInput
          size="m"
          label="Limit Name"
          placeholder="Enter Limit Name"
          rightSection={
            <CloseButton
              aria-label="Clear input"
              variant="transparent"
              size="md"
              mr="5px"
              style={{
                display: search ? undefined : "none",
              }}
              onClick={() => setSearch("")}
            />
          }
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </SimpleGrid>
      <Table
        columns={columns}
        rows={rows}
        defaultSort={{
          columnAccessor: "createdAt",
          direction: SortDirection.asc,
        }}
        loading={fetching}
        pagination={{
          pageCount: pageCount,
          page: page,
          onPageChange: handlePageChange,
        }}
        onRowClick={handleRowClik}
        onSortChange={handleSortChange}
      />
    </Flex>
  );
};

export default NewCURecommendationTable;
